import { useNavigate } from 'react-router-dom'

import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
  useUnsafeEffect,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaCostCenterStepDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { factorialCostCentersToOptions } from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'
import {
  A3InnuvaMapping,
  A3InnuvaWizardData,
} from 'modules/payroll_integrations_a3innuva/lib/types'
import { type Action } from 'modules/questionnaires/components/Questionnaire/Actions'
import EmptyState from 'modules/questionnaires/components/Questionnaire/EmptyState'

import { CostCenterItem } from '../CostCenterItem'

const getCompanyCodes = (answers: { [key: string]: string }): string[] => {
  if (answers.company_code_enabled === 'true') {
    return answers.company ? [answers.company] : []
  }

  const legalEntityMatcher = /^legal_entity_\d+$/
  const legalEntityKeys = Object.keys(answers).filter((key) => legalEntityMatcher.test(key))
  const legalEntityCodes = legalEntityKeys.map((key) => answers[key])

  return legalEntityCodes.filter(Boolean)
}

const CostCenterStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const session = useCurrentSession()
  const { loading, data: costCenterData } = useQuery(A3InnuvaCostCenterStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      legalEntityIds: session.company.legalEntities.map(({ id }) => id),
      companyCodes: getCompanyCodes(data.answers),
    },
  })
  const a3CostCenters = costCenterData?.payrollIntegrationsA3innuva.costCenterCodes ?? []
  const factorialCostCenters = costCenterData?.finance.costCenters ?? []
  const options = factorialCostCentersToOptions(a3CostCenters)

  const answer_keys = Object.keys(data.answers)

  const costCenterFromAnswers: A3InnuvaMapping[] = answer_keys
    .map((key) => {
      if (key.includes('cost_center_')) {
        const new_key = key.split('cost_center_')[1]
        return { factorial_id: new_key, a3innuva_id: data.answers[key] }
      }
    })
    .filter(Boolean)

  useUnsafeEffect(() => {
    if (data.sources?.cost_centers === undefined) {
      onDataChanged({
        ...data,
        sources: {
          ...data.sources,
          cost_centers: {
            mappings: costCenterFromAnswers,
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const costCentersSource = data.sources?.cost_centers
  const mappings: A3InnuvaMapping[] = costCentersSource?.mappings || []

  const onMappingChanged = (newMappings: A3InnuvaMapping[]) => {
    onDataChanged({
      ...data,
      sources: {
        ...data.sources,
        cost_centers: {
          ...costCentersSource,
          mappings: newMappings,
        },
      },
    })
  }

  const costCenters = data?.sources?.cost_centers?.mappings || []
  const costCentersCount = costCenters.length

  const costCenterMappings = costCenters.reduce(
    (acc, mapping) => ({
      ...acc,
      [`cost_center_${mapping.factorial_id}`]: mapping.a3innuva_id,
    }),
    {}
  )

  useOnNext(() => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        ...costCenterMappings,
      },
    })

    // const costCenters = Object.keys(data.answers).filter((key) => key.startsWith('cost_center_'))

    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsNumberOfContractTypesMapped, {
      company_id: session.company.id,
      access_id: session.access.id,
      cost_centers_mapped: costCentersCount,
    })
  })

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const actions: Action[] = [
    {
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.cost_center.no_cost_centers_action'
      ),
      type: 'primary',
      onClick: () => navigate(navigation.settings.finance.costCenters.fullPath()),
      disabled: false,
    },
  ]

  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.cost_center.title'
          )}
          description={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.cost_center.description'
          )}
        />
        {loading ? (
          <Spinner />
        ) : factorialCostCenters.length ? (
          <CostCenterItem
            costCenters={factorialCostCenters}
            mappings={mappings}
            options={options}
            onMappingChanged={onMappingChanged}
            loading={loading}
          />
        ) : (
          <Box maxWidth="s600">
            <EmptyState
              illustration="place"
              title={i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.cost_center.no_cost_centers_title'
              )}
              description={i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.cost_center.no_cost_centers_description'
              )}
              actions={actions}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export const costCenterStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.cost_center.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <CostCenterStep {...props} />,
  isCompleted: (_data) => true,
})
